/**
 * Makes an API request using axios library.
 *
 * @async
 * @function
 * @param {Object} options - The axios request configuration object.
 * @param {boolean} [options.isFileUpload] - Set to true to send as multipart/form-data.
 * @returns {Promise<Object>} - The response data from the API.
 * @throws {Object} - The error object containing status and message properties.
 */
import axios, {
  isAxiosError,
  type AxiosProgressEvent,
  type AxiosRequestConfig,
} from 'axios';
import { clearCookies, getToken } from '../../utils/helpers/cookies.helpers';
import { miaConsoleLog } from '../../utils/mia-logger';

interface ApiClientOptions extends AxiosRequestConfig {
  isFileUpload?: boolean;
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
}

const apiClient = async ({
  isFileUpload = false,
  onUploadProgress,
  ...options
}: ApiClientOptions) => {
  // `npm run dev:expose` routes API calls through the Vite proxy (see
  // vite.config.ts) so they are same-origin: the API allowlists
  // http://localhost:5173 only, which otherwise blocks testing from a LAN IP
  // or a tunnel. Plain `npm run dev` and production call the API directly.
  const useApiProxy =
    import.meta.env.DEV && import.meta.env.VITE_USE_API_PROXY === 'true';
  const baseURL = useApiProxy ? '/api' : import.meta.env.VITE_API_BASE_URL;

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': isFileUpload ? 'multipart/form-data' : 'application/json',
  };

  const client = axios.create({ baseURL, headers });

  const token = getToken();
  if (token) client.defaults.headers.common.Authorization = `Bearer ${token}`;

  client.interceptors.response.use(
    (response) => {
      const method = (response.config.method ?? 'GET').toUpperCase();
      const url = `${baseURL}/${options.url ?? ''}`;
      let requestBody: unknown;
      try { requestBody = response.config.data ? JSON.parse(response.config.data) : undefined; } catch { /* ignore */ }
      miaConsoleLog(
        'trace',
        {
          message: `${method} ${url}`,
          request: {
            headers: { ...(response.config.headers ?? {}) },
            ...(requestBody !== undefined ? { body: requestBody } : {}),
          },
          response: { status: response.status, body: response.data },
        },
        { type: 'http', sensitiveFields: ['request.headers.Authorization'] },
      );
      return response;
    },
    (error) => {
      if (isAxiosError(error)) {
        const status = error.response?.status ?? 0;
        const level = status >= 500 ? 'error' : 'warn';
        const method = (error.config?.method ?? 'GET').toUpperCase();
        const url = `${baseURL}/${options.url ?? ''}`;
        miaConsoleLog(
          level,
          {
            message: `${method} ${url}`,
            request: { headers: { ...(error.config?.headers ?? {}) } },
            response: { status, body: error.response?.data },
          },
          { type: 'http', sensitiveFields: ['request.headers.Authorization'] },
        );
      }
      return Promise.reject(error);
    },
  );

  try {
    const response = await client({
      ...options,
      ...(isFileUpload && onUploadProgress && { onUploadProgress }),
    });

    if (!response.data) throw new Error('API Request Was Not Successful');

    return response.data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      if (error.response?.status === 401) clearCookies();

      const message =
        error.response?.data?.error?.message ??
        error.response?.data?.message ??
        error.message;

      throw Object.assign(new Error(message), {
        status: error.response?.status,
      });
    }

    throw Object.assign(
      new Error(
        error instanceof Error ? error.message : 'An unexpected error occurred',
      ),
      { status: undefined },
    );
  }
  // } catch (error: unknown) {
  //   if (isAxiosError(error)) {
  //     if (error.response?.status === 401) clearCookies();
  //     // debugger;
  //     throw {
  //       status: error.response?.status,
  //       message: error.response?.data?.message ?? error.response?.data?.message?.error?.message ?? error.message,
  //     };
  //   }

  //   throw {
  //     status: undefined,
  //     message:
  //       error instanceof Error ? error.message : 'An unexpected error occurred',
  //   };
  // }
};

export default apiClient;

/**
 * Makes an API request using axios library.
 *
 * @async
 * @function
 * @param {Object} options - The axios request configuration object.
 * @returns {Promise<Object>} - The response data from the API.
 * @throws {Object} - The error object containing status and message properties.
 */
// import axios /*, { isAxiosError }*/ from 'axios';
// // import type { AxiosError } from 'axios';
// import { clearCookies, getToken } from '../../utils/helpers/cookies.helpers';

// const apiClient = async ({ ...options }) => {
//   const baseURL = import.meta.env.VITE_API_BASE_URL;
//   console.log('🚀 ~ apiClient ~ baseURL:', baseURL);

//   const headers = {
//     Accept: 'application/json',
//     'Content-Type': 'application/json',
//   };

//   const client = axios.create({ baseURL, headers });

//   let token = getToken();

//   if (token) client.defaults.headers.common.Authorization = `Bearer ${token}`;

//   try {
//     const response = await client(options);

//     if (!response.data) throw new Error('API Request Was Not Successful');

//     return response.data;
//   } catch (error: any) {
//     if (error?.response?.status === 401) clearCookies();

//     const errorObj = {
//       status: error.response?.status,
//       message: error.response?.data?.message || error.message?.message || error,
//     };

//     throw errorObj;
//   }
// };

// export default apiClient;
